<template>
  <div>
    <transition name="modal-fade">
      <div v-if="showFinanceModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 pb-2">
        <div class="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
             @click="showFinanceModal = false"></div>

        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div class="flex justify-between items-center p-5 bg-gray-100 border-b">
            <h3 class="text-xl font-bold text-gray-800">اطلاعات مالی سفارش #{{ order.id }}</h3>
            <button @click="showFinanceModal = false" class="p-2 text-gray-500 hover:text-gray-700">
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>

          <div class="overflow-y-auto max-h-[calc(90vh-120px)] p-4 space-y-6">
            <!-- Bank Payment Section -->
            <div v-if="order.bank_payment" class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">پرداخت بانکی</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p class="text-sm text-gray-600">وضعیت:</p>
                  <span class="px-6 py-3 rounded-full text-xl font-medium"
                        :class="getPaymentStatusClass(order.bank_payment.status)">
                          {{ getPaymentStatusText(order.bank_payment.status) }}
                        </span>
                </div>
                <div>
                  <p class="text-sm text-gray-600">مبلغ:</p>
                  <p class="font-medium">{{ order.bank_payment.amount | toMoney }} تومان</p>
                  <p class="text-sm text-gray-600">کارمزد:</p>
                  <p class="font-medium">{{ (order.bank_payment.fee / 10) | toMoney }} تومان</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">درگاه پرداخت:</p>
                  <p class="font-medium">{{ order.bank_payment.gateway || '---' }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">شماره پیگیری:</p>
                  <p class="font-medium">{{ order.bank_payment.reference_id || '---' }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">تاریخ پرداخت:</p>
                  <p class="font-medium">{{ order.bank_payment.paid_at }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">کارت پرداخت کننده:</p>
                  <p class="font-medium">{{ order.bank_payment.card_pan || '---' }}</p>
                </div>
              </div>
            </div>
            <div v-else class="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
              اطلاعات پرداخت بانکی موجود نیست
            </div>

            <div>
              <h4 class="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">تراکنش‌های ولت</h4>
              <div v-if="order.transaction && order.transaction.length" class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-gray-100">
                  <tr>
                    <th class="py-2 px-4 text-right text-xs font-medium text-gray-600 uppercase">نوع تراکنش</th>
                    <th class="py-2 px-4 text-right text-xs font-medium text-gray-600 uppercase">مبلغ</th>
                    <th class="py-2 px-4 text-right text-xs font-medium text-gray-600 uppercase">وضعیت</th>
                    <th class="py-2 px-4 text-right text-xs font-medium text-gray-600 uppercase">تاریخ</th>
                    <th class="py-2 px-4 text-right text-xs font-medium text-gray-600 uppercase">توضیحات</th>
                  </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                  <tr v-for="(txn, index) in order.transaction" :key="index" class="hover:bg-gray-50">
                    <td class="py-3 px-4 text-right text-sm text-gray-700">
                      {{ getTransactionTypeText(txn.type) }}
                    </td>
                    <td class="py-3 px-4 text-right text-sm font-medium">
                      {{ txn.amount | toMoney }} تومان
                    </td>
                    <td class="py-3 px-4 text-right">
                    <span class="px-2 py-1 rounded-full text-xs" :class="getTransactionStatusClass(txn.status)">
                      {{ getTransactionStatusText(txn.status) }}
                    </span>
                    </td>
                    <td class="py-3 px-4 text-right text-sm text-gray-500">
                      {{ txn.created_at }}
                    </td>
                    <td class="py-3 px-4 text-right text-sm text-gray-500">
                      {{ txn.description || '---' }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                تراکنشی برای این سفارش ثبت نشده است
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showHistoryModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Glass Overlay -->
        <div class="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
             @click="showHistoryModal = false"></div>

        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div class="flex justify-between items-center p-5 bg-gray-100 border-b">
            <h3 class="text-xl font-bold text-gray-800">تاریخچه تغییرات سفارش #{{ order.id }}</h3>
            <button @click="showHistoryModal = false"
                    class="p-2 text-gray-500 hover:text-gray-700">
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>

          <div class="overflow-y-auto max-h-[calc(90vh-120px)]">
            <table class="w-full">
              <thead class="bg-gray-50">
              <tr>
                <th class="py-3 px-4 text-right text-xs font-medium text-gray-600 uppercase">تاریخ تغییر</th>
                <th class="py-3 px-4 text-right text-xs font-medium text-gray-600 uppercase">تغییر دهنده</th>
                <th class="py-3 px-4 text-right text-xs font-medium text-gray-600 uppercase">وضعیت قبلی</th>
                <th class="py-3 px-4 text-right text-xs font-medium text-gray-600 uppercase">وضعیت جدید</th>
              </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
              <tr v-for="(history, index) in order.history.slice().reverse()" :key="index"
                  class="hover:bg-gray-50">
                <td class="py-3 px-4 text-right text-sm text-gray-700">
                  {{ history.changed_at }}
                </td>
                <td class="py-3 px-4 text-right text-sm text-gray-700">
                  {{ history.changed_by || 'سیستم' }}
                </td>
                <td class="py-3 px-4 text-right">
                <span class="px-2 py-1 rounded-full text-xs" :class="getStatusClass(history.previous_status)">
                  {{ getStatusText(history.previous_status) }}
                </span>
                </td>
                <td class="py-3 px-4 text-right">
                <span class="px-2 py-1 rounded-full text-xs" :class="getStatusClass(history.new_status)">
                  {{ getStatusText(history.new_status) }}
                </span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showStatusModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" @click="showStatusModal=false"></div>
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
          <h3 class="text-lg font-bold mb-4">تغییر وضعیت سفارش</h3>

          <label class="block text-sm text-gray-600 mb-1">وضعیت مقصد</label>
          <select v-model="statusForm.target"
                  class="w-full border rounded-lg p-2 mb-4 bg-blue-50">
            <option :value="null" disabled>انتخاب کنید…</option>
            <option v-for="opt in switchableStatusOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>

          <label class="block text-sm text-gray-600 mb-1">یادداشت (اختیاری)</label>
          <textarea v-model="statusForm.note" rows="3"
                    class="w-full border rounded-lg p-2 mb-4 bg-blue-50"
                    placeholder="دلیل تغییر وضعیت…"></textarea>

          <div class="flex gap-2 justify-end">
            <button @click="showStatusModal=false" class="px-3 py-2 rounded-lg border">انصراف</button>
            <button @click="submitStatusChange" class="px-3 py-2 rounded-lg bg-blue-600 text-white">
              تایید و اعمال
            </button>
          </div>
        </div>
      </div>
    </transition>
    <div class="p-6 backdrop-blur-lg bg-white/60 border border-white/30 rounded-xl">
      <router-link v-if="user.user_type === 'suprt'" :to="{ name: 'SupportOrdersList' }"
                   class="inline-flex items-center gap-1 mb-4 text-sm text-gray-600 hover:text-gray-900">
        &#8592; بازگشت به سفارشات
      </router-link>
      <!-- Order Header Section -->
      <div class="bg-white rounded-xl shadow-md p-6 mb-6 flex items-center justify-between">
        <div class="flex items-center space-x-4 space-x-reverse">
          <h1 class="text-2xl font-bold text-gray-800">سفارش {{ order.id }}</h1>
          <span class="px-3 py-1 rounded-full text-sm font-medium" :class="statusClass">
          {{ statusText }}
        </span>
        </div>
        <div class="flex items-center space-x-4 space-x-reverse">
          <div class="text-right">
            <span class="text-sm text-gray-500">تاریخ ثبت:</span>
            <span class="text-sm font-medium text-gray-700 mr-1">{{ order.date_time }}</span>
          </div>
        </div>
        <div class="flex items-center space-x-4 space-x-reverse">
          <div class="text-right">
            <span class="text-sm text-gray-500">تعداد مدل کالا:</span>
            <span class="text-sm font-medium text-gray-700 mr-1">{{ totalProduct | toMoney }}</span>
          </div>
        </div>
        <div class="flex items-center space-x-4 space-x-reverse">
          <div class="text-right">
            <span class="text-sm text-gray-500">جمع تعداد اقلام :</span>
            <span class="text-sm font-medium text-gray-700 mr-1">{{ order.total_product_quantity | toMoney }}</span>
          </div>
        </div>
        <div class="left-2">
          <button v-if="user.user_type !== 'suprt'" @click="openStatusModal"
                  class="ml-2 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg text-sm ">
            تغییر وضعیت
          </button>

          <button @click="showHistoryModal = true"
                  class="bg-blue-500 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition-all duration-300 ease-in-out transform hover:hover:scale-105 hover:bg-gradient-to-r hover:from-purple-500 hover:via-pink-500 hover:to-blue-500 hover:shadow-md hover:animate-pulse">
            <i class="fas fa-history"></i>
          </button>

          <button @click="showFinanceModal = true" v-if="order.status != 'pe' && order.status != 'ex'"
                  class=" mr-2 bg-green-500 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-all duration-300 ease-in-out transform hover:scale-105 hover:bg-gradient-to-r hover:from-green-500 hover:to-teal-500 hover:shadow-md">
            <i class="fas fa-money-bill-wave"></i>
          </button>

          <button @click="printOrder"
                  class="mr-2 bg-purple-500 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition-all duration-300 ease-in-out transform hover:hover:scale-105 hover:bg-gradient-to-r hover:from-purple-500 hover:via-pink-500 hover:to-blue-500 hover:shadow-md hover:animate-pulse">
            <i class="fas fa-print"></i>
          </button>
        </div>

      </div>

      <!-- Order Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div
            class="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-all duration-300">
          <div class="flex items-center space-x-3 space-x-reverse">
            <div class="p-3 rounded-lg bg-gradient-to-r from-purple-100 to-blue-100 text-purple-500">
              <i class="fas fa-user text-lg"></i>
            </div>
            <div class="min-w-0">
              <p class="text-xs text-gray-500">مشتری</p>
              <p class="text-[10px] font-medium text-gray-700 break-words whitespace-normal truncate hover:whitespace-normal">
                {{ order.shipment_address.first_name }}
                {{ order.shipment_address.last_name }}
                {{ order.customer.mobile_number }}
              </p>
            </div>
          </div>
        </div>


        <div
            class="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-all duration-300">
          <div class="flex items-center space-x-3 space-x-reverse">
            <div class="p-3 rounded-lg bg-gradient-to-r from-green-100 to-teal-100 text-green-500">
              <i class="fas fa-map-marker-alt text-lg"></i>
            </div>
            <div class="min-w-0">
              <p class="text-xs text-gray-500">آدرس ارسال</p>
              <p class="text-[10px] font-medium text-gray-700 break-words whitespace-normal truncate hover:whitespace-normal">
                {{ order.shipment_address.state }}
                {{ order.shipment_address.city }}
                {{ order.shipment_address.address }}
                {{ order.shipment_address.house_number || ' ' }}
                {{ order.shipment_address.house_unit || ' ' }}
                <span v-if="order.shipment_address.zip_code">
                کدپستی:
                {{ order.shipment_address.zip_code }}
              </span>
              </p>
            </div>
          </div>
        </div>

        <div
            class="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-all duration-300">
          <div class="flex items-center space-x-3 space-x-reverse">
            <div class="p-3 rounded-lg bg-gradient-to-r from-orange-100 to-amber-100 text-orange-500">
              <i class="fas fa-barcode text-lg"></i>
            </div>
            <div class="min-w-0">
              <p class="text-xs text-gray-500"> کد رهگیری اکسونی</p>
              <p class="text-sm font-medium text-gray-700 break-words whitespace-normal truncate hover:whitespace-normal">{{ order.exuni_tracking_code }}</p>
            </div>
          </div>
        </div>
        <div
            class="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-all duration-300">
          <div class="flex items-center space-x-3 space-x-reverse">
            <div class="p-3 rounded-lg bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-500">
              <i class="fas fa-truck text-lg"></i>
            </div>
            <div>
              <p class="text-xs text-gray-500">کد رهگیری پستی</p>
              <p class="text-sm font-medium text-gray-700"> {{ order.post_tracking_code || 'وارد نشده' }} </p>
            </div>
          </div>
        </div>

      </div>

      <!-- Order Items Table -->
      <div class="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-100">
            <tr>
              <th class="text-center py-2" width="50">
                <span style="font-size: xx-small;">ردیف</span>
              </th>
              <th class="text-center py-2" width="110">
                <span style="font-size: x-small">شناسه کالا</span>
              </th>
              <th class="text-center py-2" width="70">
                <span style="font-size: x-small">تصویر کالا</span>
              </th>
              <th class="text-center pr-5" width="80">
                <span style="font-size: x-small">تعداد کالا</span>
              </th>
              <th class="text-center py-2">
                <span style="font-size: x-small">نام کالا</span>
              </th>
              <th class="text-center py-2">
                <span style="font-size: x-small">قیمت کالا</span>
              </th>
              <th class="text-center py-2">
                <span style="font-size: x-small">مبلغ کل</span>
              </th>
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
            <tr v-for="(item, index) in displayItems" :key="index" class="hover:bg-gray-50" :class="{ 'bg-amber-50': item.parent_bundle_name }">
              <td class="text-center py-3" width="50">
                <span style="font-size: xx-small">{{ index + 1 }}</span>
              </td>
              <td class="text-center py-3" width="110">
                <span style="font-size: x-small; font-weight: bold">
                  {{ item.sixteen_digit_code || '-' }}
                </span>
              </td>
              <td class="text-center py-3" width="100">
                <img v-if="item.image" class="rounded-lg" width="70" :src="img_source(item.image)">
              </td>
              <td class="text-center pr-5" width="80">
                <span style="font-weight: bold">
                  {{ item.quantity | toMoney }}
                </span>
                <span style="font-size: small">
                  عدد
                </span>
              </td>
              <td class="text-center py-3">
                <div v-if="item.parent_bundle_name">
                  <p style="font-size: xx-small; color: #b45309;">
                    از پکیج «{{ item.parent_bundle_name }}»
                  </p>
                </div>
                <div v-else-if="item.variation_of_name">
                  <p style="font-size: xx-small">
                    {{ item.variation_of_name }}
                  </p>
                </div>
                <p style="font-weight: bold; font-size: x-small">
                  {{ item.product_name || '-' }}
                </p>
              </td>
              <td class="text-center py-3">
                <template v-if="item.price != null">
                  <p style="font-size: x-small">
                  <span style="font-weight: bold">
                    {{ item.price | toMoney }}
                  </span>
                    <span style="font-size: small">
                    تومان
                  </span>
                  </p>
                  <div v-if="item.regular_price != item.price" class="price-container"
                       style="font-size: xx-small">
                    <span>{{ item.regular_price | toMoney }}</span>
                    <div class="absolute top-1/2 left-0 right-0 h-px bg-gray-400 transform -translate-y-1/2"></div>
                  </div>
                </template>
                <span v-else style="font-size: x-small; color: #9ca3af;">—</span>
              </td>
              <td class="text-center py-3">
                <template v-if="item.line_total != null">
                  <span style="font-weight: bold">
                    {{ item.line_total | toMoney }}
                  </span>
                  <span style="font-size: small">
                    تومان
                  </span>
                </template>
                <span v-else style="font-size: x-small; color: #9ca3af;">—</span>
              </td>
            </tr>
            <tr class="bg-gray-50">
              <td class="text-center py-3" width="50">
                <span style="font-size: xx-small; font-weight: bold">جمع</span>
              </td>
              <td class="text-center py-3" colspan="2"></td>
              <td class="text-center pr-5" width="80">
              <span style="font-weight: bold; font-size: x-small">
                {{ totalQuantity | toMoney }}
              </span>
                <span style="font-size: xx-small">
                عدد
              </span>
              </td>
              <td class="text-center py-3"></td>
              <td class="text-center py-3"></td>
              <td class="text-center py-3">
              <span style="font-weight: bold; font-size: x-small">
                {{ totalPrice | toMoney }}
              </span>
                <span style="font-size: xx-small">
                تومان
              </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Order Totals -->
      <div class="bg-white rounded-xl shadow-sm p-6 float-left">
        <div class="space-y-3 max-w-md ml-auto">
          <div class="flex justify-between ">
            <span class="text-sm text-gray-600">جمع کل:</span>
            <span class="text-sm font-medium text-gray-700 mr-5">{{ order.total_price | toMoney }}
            <span style="font-size: xx-small">
              تومان
            </span>

          </span>
          </div>
          <div v-if="order.discount_amount > 0" class="flex justify-between ">
            <span class="text-sm text-gray-600">تخفیف:</span>
            <span class="text-sm font-medium text-red-500 mr-5">-{{ order.discount_amount | toMoney }}
            <span style="font-size: xx-small">
              تومان
            </span>
          </span>
          </div>
          <div class="flex  justify-between">
            <span class="text-sm text-gray-600">هزینه ارسال:</span>
            <span class="text-sm font-medium text-gray-700 mr-5 ">{{ order.post_price | toMoney }}
            <span style="font-size: xx-small">
            تومان
          </span>

          </span>
          </div>
          <div class="flex items-center justify-between mt-3">
            <span class="text-sm text-gray-600">هزینه واقعی ارسال</span>
            <span class="text-sm font-medium text-gray-700">
              <template v-if="order.actual_shipping_cost != null">{{ order.actual_shipping_cost | toMoney }} تومان</template>
              <template v-else>ثبت نشده</template>
            </span>
          </div>

          <div class="text-sm flex  justify-between">
            <span class=" text-gray-700">مبلغ کل فاکتور:</span>
            <span class=" font-bold text-green-500 mr-5 ">
            {{ order.final_amount | toMoney }}
            <span style="font-size: xx-small">
              تومان
            </span>
          </span>
          </div>
          <div @click="showFinanceModal=true" style="cursor: pointer" class=" text-sm flex  justify-between" v-if="order.bank_payment">
            <span class=" text-gray-700">مبلغ کارمزد بانک:</span>
            <span class=" text-sm font-medium text-gray-700 mr-5">
            {{ (order.bank_payment.fee / 10) | toMoney }}
            <span style="font-size: xx-small">
              تومان
            </span>
          </span>
          </div>
          <div class=" text-sm flex  justify-between" v-if="order.bank_payment">
            <span class=" text-gray-700">مبلغ نهایی:</span>
          <span class=" font-bold text-green-500 mr-5 ">
            {{ ((order.bank_payment.fee / 10) + order.final_amount) | toMoney }}
            <span style="font-size: xx-small">
              تومان
            </span>
          </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderDetail',
  props: {
    id: {},
  },
  data() {
    return {
      order: {},
      showHistoryModal: false,
      showFinanceModal: false,
      showStatusModal: false,
      statusForm: {
        target: null,
        note: ''
      }
    }
  },
  computed: {
    switchableStatusOptions() {
      const s = this.order?.status
      const map = {
        'pa': [ 'pr', 'sh' ],
        'pr': [ 'pa', 'sh' ],
        'sh': [ 'pa', 'pr' ],
      }
      const labels = {
        'pa': 'جاری',
        'pr': 'درحال بسته‌بندی',
        'sh': 'ارسال شده',
      }
      return (map[s] || []).map(v => ({ value: v, label: labels[v] }))
    },
    totalQuantity() {
      return this.order.total_product_quantity
    },
    displayItems() {
      // پکیج‌ها اینجا به اجزاشون شکسته می‌شن تا نیروی جمع‌آوری دقیقاً بدونه چی برداره.
      // اگر بک‌اند هنوز exploded_items رو برنگردونه (قبل از دیپلوی)، همون order.items
      // قدیمی رو به همین شکل یکسان تبدیل می‌کنیم تا جدول خراب نشه.
      if (Array.isArray(this.order.exploded_items)) return this.order.exploded_items;
      return (this.order.items || []).map((item) => ({
        product_name: item.product && item.product.name,
        sixteen_digit_code: item.product && item.product.sixteen_digit_code,
        image: item.product && item.product.image,
        quantity: item.product_quantity,
        parent_bundle_name: null,
        variation_of_name: item.product && item.product.variation_of_name,
        price: item.price,
        regular_price: item.product && item.product.regular_price,
        line_total: (item.price || 0) * item.product_quantity,
      }));
    },
    totalPrice() {
      return this.order.items?.reduce((sum, item) => sum + (item.price * item.product_quantity), 0) || 0
    },
    totalProduct() {
      return this.order.items.length
    },

    statusText() {
      const statusMap = {
        'pe': 'در انتظار پرداخت',
        'pa': 'جاری',
        'pr': 'درحال بسته بندی',
        'pc': 'بسته بندی شد',
        'sh': 'ارسال شده',
        'de': 'تحویل شده',
        'ca': 'لغو شده',
        'ex': 'منقضی شده',
        're': 'مرجوعی'
      }
      return statusMap[this.order.status] || '---'
    },
    statusClass() {
      const classes = {
        'pe': 'bg-amber-100 text-amber-800',
        'pa': 'bg-blue-100 text-blue-800',
        'pr': 'bg-indigo-100 text-indigo-800',
        'pc': 'bg-teal-100 text-teal-800',
        'sh': 'bg-purple-100 text-purple-800',
        'de': 'bg-green-100 text-green-800',
        'ca': 'bg-red-100 text-red-800',
        'ex': 'bg-gray-100 text-gray-800',
        're': 'bg-pink-100 text-pink-800'
      }
      return classes[this.order.status] || 'bg-gray-100 text-gray-800'
    },
    shippingAddress() {
      const address = this.order.shipment_address
      return ` ${address.state}، ${address.city}، ${address.address}، کدپستی: ${address.zip_code}`
    },
  },
  methods: {
    openStatusModal() {
      this.statusForm = { target: null, note: '' }
      this.showStatusModal = true
    },
    submitStatusChange() {
      if (!this.statusForm.target) return this.$toast?.error?.('وضعیت مقصد را انتخاب کنید')

      this.request({
        url: this.endpoint(`shop/shopOrders/${this.order.id}/setStatus`),
        method: 'post',
        data: {
          new_status_code: this.statusForm.target,
          note: this.statusForm.note
        },
        success: (res) => {
          this.fetchOrder()
          this.showStatusModal = false
          this.successNotify()
        }
      })
    },
    formatPrice(price) {
      return new Intl.NumberFormat('fa-IR').format(price)
    },
    fetchOrder() {
      this.request({
        url: this.endpoint(`shop/adminOrderDetail/${this.id}`),
        method: "get",
        success: data => {
          this.order = data
        }
      })
    },
    printOrder() {
      let print_url = `https://api.exuni.ir/shop/adminShopOrderListView/html?token=${this.token}&exuni_tracking_code=${this.order.exuni_tracking_code}`
      window.open(print_url)
    },
    getStatusText(status) {
      const statusMap = {
        'pe': 'در انتظار پرداخت',
        'pa': 'جاری',
        'pr': 'درحال بسته بندی',
        'pc': 'بسته بندی شد',
        'sh': 'ارسال شده',
        'de': 'تحویل شده',
        'ca': 'لغو شده',
        'ex': 'منقضی شده',
        're': 'مرجوعی'
      }
      return statusMap[status] || '---'
    },

    getStatusClass(status) {
      const classes = {
        'pe': 'bg-amber-100 text-amber-800',
        'pa': 'bg-blue-100 text-blue-800',
        'pr': 'bg-indigo-100 text-indigo-800',
        'pc': 'bg-teal-100 text-teal-800',
        'sh': 'bg-purple-100 text-purple-800',
        'de': 'bg-green-100 text-green-800',
        'ca': 'bg-red-100 text-red-800',
        'ex': 'bg-gray-100 text-gray-800',
        're': 'bg-pink-100 text-pink-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    },
    getPaymentStatusClass(status) {
      const classes = {
        'in': 'bg-blue-100 text-blue-800',      // Initiated
        'pe': 'bg-yellow-100 text-yellow-800',  // Pending
        'su': 'bg-green-100 text-green-800',    // Success
        'fa': 'bg-red-100 text-red-800',        // Failed
        'ex': 'bg-gray-100 text-gray-800',      // Expired
        'ca': 'bg-purple-100 text-purple-800'   // Cancelled
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    },

    getPaymentStatusText(status) {
      const statusMap = {
        'in': 'شروع شده',
        'pe': 'در حال انجام',
        'su': 'موفق',
        'fa': 'ناموفق',
        'ex': 'منقضی شده',
        'ca': 'لغو شده'
      }
      return statusMap[status] || status
    },

    getTransactionTypeText(type) {
      const typeMap = {
        'top-up': 'شارژ ولت',
        'withdraw': 'برداشت',
        'payment': 'پرداخت',
        'buy': 'خرید از ولت',
        'transfer': 'انتقال',
        'investment': 'سرمایه گذاری',
        'refund': 'برگشت مبلغ سفارش'
      }
      return typeMap[type] || type
    },

    getTransactionStatusText(status) {
      const statusMap = {
        'pending': 'بلاتکلیف',
        'success': 'موفق',
        'canceled': 'لغو سفارش',
        'failed': 'ناموفق'
      }
      return statusMap[status] || status
    },

    getTransactionStatusClass(status) {
      const classes = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'success': 'bg-green-100 text-green-800',
        'canceled': 'bg-red-100 text-red-800',
        'failed': 'bg-gray-100 text-gray-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }


  },
  mounted() {
    this.fetchOrder();
  }

}
</script>

<style scoped>

.price-container {
  position: relative;
  display: inline-block;
}

.price-container::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #6b7280;
  transform: translateY(-50%);
}

/* For RTL support */
[dir="rtl"] .space-x-reverse > :not([hidden]) ~ :not([hidden]) {
  --tw-space-x-reverse: 1;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Glass effect enhancement */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
}

.backdrop-blur-lg {
  backdrop-filter: blur(16px);
}

/* Smooth scrolling for modal content */
.max-h-\[calc\(90vh-120px\)\] {
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
}

.max-h-\[calc\(90vh-120px\)\]::-webkit-scrollbar {
  width: 6px;
}

.max-h-\[calc\(90vh-120px\)\]::-webkit-scrollbar-track {
  background: transparent;
}

.max-h-\[calc\(90vh-120px\)\]::-webkit-scrollbar-thumb {
  background-color: rgba(155, 155, 155, 0.5);
  border-radius: 20px;
}

</style>